import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { Toaster } from "@/components/ui/sonner";
import AnnouncementBar from "@/components/AnnouncementBar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileNav from "@/components/MobileNav";
import WhatsAppButton from "@/components/WhatsAppButton";
import HomePage from "@/pages/HomePage";
import ScrollToTop from "@/components/ScrollToTop";

// Route-based code splitting — admin bundle stays out of the public/storefront chunk.
// webpackPrefetch hints let the browser fetch the shop/product/cart/checkout bundles during
// idle time after the homepage finishes loading, so the second navigation is near-instant.
const ShopPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/ShopPage"));
const ProductPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/ProductPage"));
const CartPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/CartPage"));
const CheckoutPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/CheckoutPage"));
const OrderTrackPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/OrderTrackPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const MagicLoginPage = lazy(() => import("@/pages/MagicLoginPage"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/Products"));
const AdminCategories = lazy(() => import("@/pages/admin/Categories"));
const AdminOrders = lazy(() => import("@/pages/admin/Orders"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const Bookkeeping = lazy(() => import("@/pages/admin/Bookkeeping"));
const StaffCheckout = lazy(() => import("@/pages/admin/StaffCheckout"));
const StaffManagement = lazy(() => import("@/pages/admin/StaffManagement"));
const AdminLegalPages = lazy(() => import("@/pages/admin/LegalPages"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminCustomers = lazy(() => import("@/pages/admin/Customers"));
const AdminInventoryLedger = lazy(() => import("@/pages/admin/InventoryLedger"));
const AdminHeroSliders = lazy(() => import("@/pages/admin/HeroSliders"));
const AdminSalesCoupons = lazy(() => import("@/pages/admin/SalesCoupons"));

function RouteFallback() {
    return (
        <div className="flex items-center justify-center min-h-[60vh]" aria-hidden="true">
            <div className="w-8 h-8 border-4 border-pink-600 border-t-transparent rounded-full animate-spin" />
        </div>
    );
}

function ProtectedRoute({ children, adminOnly = false }) {
    const { user, loading } = useAuth();
    if (loading) return <RouteFallback />;
    if (!user) return <Navigate to="/login" replace />;
    if (adminOnly && !['admin', 'staff'].includes(user.role)) return <Navigate to="/" replace />;
    return children;
}

function AppContent() {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith('/admin');
    return (
        <div className="min-h-screen flex flex-col">
            <ScrollToTop />
            {!isAdmin && <AnnouncementBar />}
            {!isAdmin && <Header />}
            <main className={`flex-1 ${!isAdmin ? 'pb-20 md:pb-0' : ''}`}>
                <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/shop" element={<ShopPage />} />
                        <Route path="/shop/:categorySlug" element={<ShopPage />} />
                        <Route path="/product/:slug" element={<ProductPage />} />
                        <Route path="/cart" element={<CartPage />} />
                        <Route path="/checkout" element={<CheckoutPage />} />
                        <Route path="/order/track/:reference" element={<OrderTrackPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
                        <Route path="/auth/magic/:token" element={<MagicLoginPage />} />
                        <Route path="/:slug" element={<LegalPage />} />
                        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
                            <Route index element={<Dashboard />} />
                            <Route path="products" element={<AdminProducts />} />
                            <Route path="categories" element={<AdminCategories />} />
                            <Route path="orders" element={<AdminOrders />} />
                            <Route path="settings" element={<AdminSettings />} />
                            <Route path="bookkeeping" element={<Bookkeeping />} />
                            <Route path="staff-checkout" element={<StaffCheckout />} />
                            <Route path="staff" element={<StaffManagement />} />
                            <Route path="users" element={<AdminUsers />} />
                            <Route path="customers" element={<AdminCustomers />} />
                            <Route path="inventory" element={<AdminInventoryLedger />} />
                            <Route path="sliders" element={<AdminHeroSliders />} />
                            <Route path="sales-coupons" element={<AdminSalesCoupons />} />
                            <Route path="legal" element={<AdminLegalPages />} />
                        </Route>
                    </Routes>
                </Suspense>
            </main>
            {!isAdmin && <Footer />}
            {!isAdmin && <MobileNav />}
            {!isAdmin && <WhatsAppButton />}
            <Toaster position="top-right" richColors />
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <CartProvider>
                    <AppContent />
                </CartProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
